import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Sparkles, Upload } from 'lucide-react';
import { extractTranscript, scanTranscripts, type ScanResult } from '../api/transcripts';

const ALLOWED_EXTS = new Set(['.vtt', '.txt']);

export default function ImportTranscriptPage() {
  const navigate = useNavigate();
  const [transcript, setTranscript] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fileError, setFileError] = useState('');
  const [loadedFileName, setLoadedFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scanError, setScanError] = useState('');

  async function handleScan() {
    setScanning(true);
    setScanResult(null);
    setScanError('');
    try {
      const result = await scanTranscripts();
      setScanResult(result);
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  }

  const trimmedLength = transcript.trim().length;
  const canSubmit = !loading && trimmedLength > 0;

  async function loadFile(file: File) {
    const dotIdx = file.name.lastIndexOf('.');
    const ext = dotIdx >= 0 ? file.name.slice(dotIdx).toLowerCase() : '';
    if (!ALLOWED_EXTS.has(ext)) {
      setFileError(`Only .vtt and .txt files are supported (got "${ext || 'no extension'}").`);
      return;
    }
    try {
      const text = await file.text();
      setTranscript(text);
      setLoadedFileName(file.name);
      setFileError('');
      setError('');
    } catch (_err) {
      setFileError('Could not read the file. Make sure it is a plain text or .vtt file.');
    }
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void loadFile(file);
    // Reset so the same file can be re-selected after clearing
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void loadFile(file);
  }

  async function handleExtract() {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const result = await extractTranscript(transcript);
      navigate('/transcripts/review', {
        state: {
          transcript_id: result.transcript_id,
          extracted_count: result.action_items.length,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Extraction failed');
      setLoading(false);
    }
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <h1 className="page-title">Import transcript</h1>
      <p className="page-subtitle">
        Paste a meeting transcript below, or upload a .vtt or .txt file. We'll extract action
        items into the review inbox for you to approve or reject before they land in the task
        board.
      </p>

      <div className="card card-padded">
        {/* File upload zone */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => !loading && fileInputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); if (!loading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 16px',
            border: `2px dashed ${dragOver ? 'var(--color-blue-accent)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius)',
            background: dragOver ? '#EFF6FF' : 'var(--color-warm-bg)',
            cursor: loading ? 'default' : 'pointer',
            marginBottom: '16px',
            transition: 'border-color 0.15s, background 0.15s',
            userSelect: 'none',
          }}
        >
          <Upload
            size={18}
            strokeWidth={1.75}
            style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }}
          />
          <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)', flex: 1 }}>
            {loadedFileName ? (
              <>
                <strong style={{ color: 'var(--color-navy-deep)' }}>{loadedFileName}</strong>
                {' '}loaded — edit below or extract
              </>
            ) : (
              'Drop a .vtt or .txt file here, or click to choose'
            )}
          </span>
          <span className="btn btn-secondary btn-sm" style={{ pointerEvents: 'none' }}>
            Choose file
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".vtt,.txt"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />

        {fileError && (
          <div
            role="alert"
            style={{
              marginBottom: '14px',
              padding: '10px 12px',
              background: 'var(--color-blocked-bg)',
              border: '1px solid var(--color-coral)',
              borderRadius: 'var(--radius)',
              color: 'var(--color-coral)',
              fontSize: '13px',
              lineHeight: 1.4,
            }}
          >
            {fileError}
          </div>
        )}

        <label
          htmlFor="transcript"
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: '8px',
          }}
        >
          Transcript
        </label>
        <textarea
          id="transcript"
          className="textarea"
          value={transcript}
          onChange={(e) => {
            setTranscript(e.target.value);
            // Clear the filename badge once the user edits manually
            if (loadedFileName) setLoadedFileName('');
          }}
          placeholder="Paste a meeting transcript here, or use the file picker above. Speaker labels (e.g. 'Aayan:') help with owner detection."
          disabled={loading}
          style={{ minHeight: 360, fontFamily: 'inherit', lineHeight: 1.55 }}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px',
            fontSize: '13px',
            color: 'var(--color-text-muted)',
          }}
        >
          <span>
            {trimmedLength.toLocaleString()} character{trimmedLength === 1 ? '' : 's'}
          </span>
          {loading && <span>Extracting action items… this can take a few seconds.</span>}
        </div>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: '16px',
              padding: '12px 14px',
              background: 'var(--color-blocked-bg)',
              border: '1px solid var(--color-coral)',
              borderRadius: 'var(--radius)',
              color: 'var(--color-coral)',
              fontSize: '14px',
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '20px',
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setTranscript('');
              setError('');
              setFileError('');
              setLoadedFileName('');
            }}
            disabled={loading || transcript.length === 0}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleExtract}
            disabled={!canSubmit}
          >
            <Sparkles size={16} strokeWidth={2} />
            {loading ? 'Extracting…' : 'Extract action items'}
          </button>
        </div>
      </div>
      <div className="card card-padded" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: scanResult || scanError ? '16px' : '0' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '2px' }}>SharePoint scan</div>
            <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
              Pull unprocessed VTT files from the Meetings folders and stage their action items for review.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleScan}
            disabled={scanning}
            style={{ flexShrink: 0, marginLeft: '16px' }}
          >
            <RefreshCw size={15} strokeWidth={2} style={scanning ? { animation: 'spin 1s linear infinite' } : undefined} />
            {scanning ? 'Scanning…' : 'Scan SharePoint'}
          </button>
        </div>

        {scanError && (
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              background: 'var(--color-blocked-bg)',
              border: '1px solid var(--color-coral)',
              borderRadius: 'var(--radius)',
              color: 'var(--color-coral)',
              fontSize: '13px',
            }}
          >
            {scanError}
          </div>
        )}

        {scanResult && (
          <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--color-green)', fontWeight: 600 }}>
                {scanResult.processed.length} processed
              </span>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {scanResult.skipped.length} skipped
              </span>
              {scanResult.failed.length > 0 && (
                <span style={{ color: 'var(--color-coral)', fontWeight: 600 }}>
                  {scanResult.failed.length} failed
                </span>
              )}
            </div>

            {scanResult.processed.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {scanResult.processed.map((f) => (
                  <div key={f.fileId} style={{ color: 'var(--color-text-secondary)' }}>
                    <span style={{ color: 'var(--color-navy-deep)', fontWeight: 500 }}>{f.fileName}</span>
                    {' '}
                    <span style={{ color: 'var(--color-text-muted)' }}>({f.folder})</span>
                    {' — '}
                    {f.stagedCount} task{f.stagedCount === 1 ? '' : 's'} staged
                  </div>
                ))}
              </div>
            )}

            {scanResult.failed.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {scanResult.failed.map((f) => (
                  <div key={f.fileId} style={{ color: 'var(--color-coral)' }}>
                    <span style={{ fontWeight: 500 }}>{f.fileName}</span>
                    {' '}
                    <span style={{ opacity: 0.75 }}>({f.folder})</span>
                    {' — '}
                    {f.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
